/**
 * Lazy-loaded "Potential Duplicates" tab for Product Catalogue Review.
 *
 * Thin fetcher — pulls the ex-product-ls-duplicate tag set for the FP and
 * hands it to the shared DataViewTable using the same duplicateConfig
 * that powers the exception report. One column/rendering source of truth.
 */
import React, { useEffect, useState } from 'react'
import { Alert, Box, CircularProgress } from '@mui/material'

import { api } from '../../../../../services/api'
import { DataViewTable } from '../../../../../components/shared/DataViewTable'
import { duplicateConfig } from '../../../../../config/exceptions/product-setup/duplicateExceptions'

interface Props {
  fp_code: string
  title?: string
}

const DuplicatesTab: React.FC<Props> = ({ fp_code, title }) => {
  const [rows, setRows] = useState<Record<string, any>[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Bumped by the table's onAfterMutation (e.g. an ignore applied in the
  // Compare Duplicates dialog) so closed groups drop from the list.
  const [refreshKey, setRefreshKey] = useState(0)

  useEffect(() => {
    if (!fp_code) return
    let cancelled = false
    setLoading(true)
    setError(null)
    api
      .get(
        `/inspector/product-setup/exceptions/ex-product-ls-duplicate/${fp_code}?limit=500&offset=0`,
      )
      .then((res: any) => {
        if (cancelled) return
        setRows(res?.inventory || res?.exceptions || [])
      })
      .catch((err: any) => {
        if (!cancelled) setError(err?.message || 'Failed to load potential duplicates')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [fp_code, refreshKey])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) return <Alert severity="error">{error}</Alert>

  if (!rows.length) {
    return (
      <Alert severity="success">
        No potential duplicate products detected. Only products sold within the
        last 12 months are compared - retired products fade from this list
        automatically.
      </Alert>
    )
  }

  return (
    <DataViewTable
      config={duplicateConfig}
      data={rows}
      variant="inspector"
      fpCode={fp_code}
      embedded
      toolbarTitle="Potential duplicates"
      title={title}
      onAfterMutation={() => setRefreshKey((k) => k + 1)}
    />
  )
}

export default DuplicatesTab
